import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  fetchPromptTemplatesWithPlan,
  createPromptTemplate,
  updatePromptTemplate,
  deletePromptTemplate,
} from '../utils/axiosApiRequest';
import { useTranslation } from 'react-i18next';
import KeywordManager from '../components/keywords/KeywordManager';

const ADD_NEW = 'ADD_NEW';

const SettingsPage = () => {
  const [templates, setTemplates] = useState([]);
  const [subscriptionPlan, setSubscriptionPlan] = useState("");
  const [selectedId, setSelectedId] = useState('');
  const [formData, setFormData] = useState({ template_name: '', prompt: '' });
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [lastSavedPrompt, setLastSavedPrompt] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    fetchPromptTemplatesWithPlan().then((data) => {
      console.log('Fetched prompt templates with plan:', data);
      setSubscriptionPlan(data.subscription_plan); // ← store subscription plan
      console.log('Subscription Plan:', data.subscription_plan);
      const templates = data.templates || [];
      setTemplates(templates);

      if (templates.length > 0) {
        const first = templates[0];
        setSelectedId(first.id);
        setFormData({ template_name: first.template_name, prompt: first.prompt });
        setOriginalPrompt(first.prompt);
      }
    });
  }, []);

  const handleSelectChange = (e) => {
    const id = e.target.value;
    setSelectedId(id);

    if (id === ADD_NEW) {
      setFormData({ template_name: '', prompt: '' });
    } else {
      const selected = templates.find((t) => t.id === id);
      if (selected) {
        setFormData({
          template_name: selected.template_name,
          prompt: selected.prompt,
        });
        setOriginalPrompt(selected.prompt);
      }
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async () => {
    if (selectedId === ADD_NEW) {
      const created = await createPromptTemplate(formData);
      setTemplates([...templates, created]);
      setSelectedId(created.id);
      setFormData({ template_name: created.template_name, prompt: created.prompt });
      setLastSavedPrompt(created.prompt);
    } else {
      const updated = await updatePromptTemplate(selectedId, formData);
      setTemplates(templates.map((t) => (t.id === selectedId ? updated : t)));
      setLastSavedPrompt(updated.prompt);
    }
  };

  const handleDelete = async () => {
    if (templates.length <= 1) return;
    await deletePromptTemplate(selectedId);
    const filtered = templates.filter((t) => t.id !== selectedId);
    setTemplates(filtered);
    setSelectedId('');
    setFormData({ template_name: '', prompt: '' });
  };

  const handleUpgradeClick = () => {
    window.open('https://billing.stripe.com/p/login/test_5kQcN481K04x7FV2nF3oA00', '_blank');
  };

  const isNew = selectedId === ADD_NEW;

   const hasUnsavedChanges = formData.prompt !== originalPrompt;

  return (
    <Box sx={{ p: 3}}>
      <Typography variant="h4" gutterBottom>
        {t('settings.title')}
      </Typography>
      <KeywordManager />
      <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom>
          {t('settings.managePromptTemplates')}
        </Typography>
        {subscriptionPlan === "pro" && (
        <>
        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel>{t('settings.selectTemplate')}</InputLabel>
            <Select value={selectedId} label="Select Template" onChange={handleSelectChange}>
              <MenuItem value={ADD_NEW}>➕ {t('settings.addTemplate')}</MenuItem>
              {templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.template_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isNew && (
            <TextField
              label={t('settings.templateName')}
              fullWidth
              value={formData.template_name}
              onChange={(e) => handleFieldChange('template_name', e.target.value)}
            />
          )}

          {(selectedId || isNew) && (
            <TextField
              label={t('settings.prompt')}
              fullWidth
              multiline
              minRows={4}
              value={formData.prompt}
              onChange={(e) => handleFieldChange('prompt', e.target.value)}
            />
          )}

          {(selectedId || isNew) && (
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isNew && !formData.template_name}
              >
                {isNew ? t('settings.createTemplate') : t('common.save')}
              </Button>
              {!isNew && hasUnsavedChanges && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setFormData({ ...formData, prompt: originalPrompt })}
                >
                  {t('settings.revertChanges')}
                </Button>
              )}
              {!isNew && templates.length > 1 && (
                <IconButton color="error" onClick={handleDelete}>
                  <DeleteIcon label={t('settings.deleteTemplate')}/>
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>
        </>
      )}
      {subscriptionPlan !== "pro" && (
        <Stack direction="row" spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            {t('settings.proRequiredForTemplates')}
          </Typography>
          <Button variant="contained" onClick={handleUpgradeClick}>
            {t('settings.upgradeToPro')}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default SettingsPage;
